import { EyeFilled, EyeInvisibleFilled } from "@ant-design/icons";
import styled from "@emotion/styled";
import type { ChartMouseEvent, OptionDataValue } from "@stepfinance/react";
import {
  Breakpoints,
  formatPercentage,
  SPACE_MONO,
  StepPieChart,
  StepSpinner,
  transparentWhite,
  white,
} from "@stepfinance/react";
import { Collapse } from "antd";
import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAccountsContext } from "../../../contexts/accounts";
import { useAppConfig } from "../../../contexts/app/config";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import { usePortfolioOverviewContext } from "../../../contexts/portfolioOverview";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { getExpandIconLeft } from "../../shared";
import type { PieDataItemWithKey } from "./PieChartLegend";
import { PieChartLegend } from "./PieChartLegend";
import { PortfolioCard, PortfolioCollapse } from "./shared";

const { Panel } = Collapse;

export const NetWorth: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const {
    networth,
    walletBalance,
    lpBalance,
    farmBalance,
    yieldAggBalance,
    NFTBalance,
    marginBalance,
    dexBalance,
    validatorBalance,
    lendingBalance,
    stakingBalance,
    managedFundsBalance,
    nftMarketsBalance,
  } = usePortfolioOverviewContext();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const navigate = useNavigate();
  const { valuesHidden, setValuesHidden } = useAppConfig();
  const location = useLocation();
  const { marketsInitialised } = useMarkets();
  const { userTokenAccounts } = useAccountsContext();

  const {
    currentLanguageData: {
      common: languageCommon,
      components: {
        table: {
          assetBreakdownPopover: { title: assetBreakdownTitle },
        },
      },
      pages: {
        dashboard: {
          netWorth: netWorthTitle,
          modules: {
            nftModule: { title: digitalCollectablesTitle },
            marginTrading: { title: marginTradingTitle },
            validatorStaking: { title: validatorStakingTitle },
            lending: { title: lendingTitle },
            managedFunds: { title: managedFundsTitle },
            nftMarkets: { title: nftMarketsTitle },
          },
        },
      },
    },
  } = useLanguageContext();

  const toggleValuesHidden = useCallback(() => setValuesHidden((value: boolean) => !value), [setValuesHidden]);

  const navigateToModule = useCallback(
    (pieDatum) => {
      const key = pieDatum.key;
      if (location.hash.substring(1) !== key) {
        navigate(`${location.pathname}#${key}`);
      }
    },
    [navigate, location.hash, location.pathname],
  );

  const filteredData = useMemo<PieDataItemWithKey[]>(() => {
    return [
      {
        value: walletBalance,
        name: languageCommon.balances,
        itemStyle: { color: "#FBC53F" },
        key: "balances",
        label: {
          show: false,
        },
      },
      {
        value: dexBalance,
        name: "DEX",
        itemStyle: { color: "#8A51A2" },
        key: "dex",
        label: {
          show: false,
        },
      },
      {
        value: lpBalance,
        name: languageCommon.liquidity,
        itemStyle: { color: "#3584D2" },
        key: "liquidity",
        label: {
          show: false,
        },
      },
      {
        value: stakingBalance,
        name: languageCommon.staking,
        itemStyle: { color: "#C2316A" },
        key: "staking",
        label: {
          show: false,
        },
      },
      {
        value: farmBalance,
        name: languageCommon.yieldFarming,
        itemStyle: { color: "#388E3C" },
        key: "farms",
        label: {
          show: false,
        },
      },
      {
        value: yieldAggBalance,
        name: languageCommon.vaults,
        itemStyle: { color: "#AFB42D" },
        key: "vaults",
        label: {
          show: false,
        },
      },
      {
        value: marginBalance,
        name: marginTradingTitle,
        itemStyle: { color: "#F59A3D" },
        key: "margin",
        label: {
          show: false,
        },
      },
      {
        value: managedFundsBalance,
        name: managedFundsTitle,
        itemStyle: { color: "#48BFE3" },
        key: "managed-funds",
        label: {
          show: false,
        },
      },
      {
        value: lendingBalance,
        name: lendingTitle,
        itemStyle: { color: "#E6633A" },
        key: "lending",
        label: {
          show: false,
        },
      },
      {
        value: validatorBalance,
        name: validatorStakingTitle,
        itemStyle: { color: "#505A9F" },
        key: "validators",
        label: {
          show: false,
        },
      },
      {
        value: NFTBalance,
        name: digitalCollectablesTitle,
        itemStyle: { color: "#1E796F" },
        key: "nft",
        label: {
          show: false,
        },
      },
      {
        value: nftMarketsBalance,
        name: nftMarketsTitle,
        itemStyle: { color: "#2dcee0" },
        key: "nft-markets",
        label: {
          show: false,
        },
      },
    ].filter((d) => d.value !== 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networth]);

  const onPieChartClick = useCallback(
    (event: ChartMouseEvent) => {
      if (event.componentType === "series") {
        navigateToModule(event.data);
      }
    },
    [navigateToModule],
  );

  const tooltipValueFormatter = useCallback(
    (value: OptionDataValue) => {
      // Get percentage of total
      const percentage = (value as number) / networth;
      return calculatePriceForDisplayCurrency(value as number) + " " + `(${formatPercentage(percentage)})`;
    },
    [calculatePriceForDisplayCurrency, networth],
  );

  return (
    <PortfolioCard>
      <h2>{netWorthTitle}</h2>
      <div className="total-value-container">
        <span className="net-worth">{calculatePriceForDisplayCurrency(networth)}</span>
        {valuesHidden ? (
          <EyeFilled style={eyeStyles} onClick={toggleValuesHidden} />
        ) : (
          <EyeInvisibleFilled style={eyeStyles} onClick={toggleValuesHidden} />
        )}
        <div className="spinner-container">
          {(!marketsInitialised || userTokenAccounts.length === 0) && <StepSpinner />}
        </div>
      </div>
      <NetworthCollapse activeKey={activePanels} ghost expandIcon={getExpandIconLeft} onChange={onPanelClick}>
        <Panel header={assetBreakdownTitle} key={getPanelKey("NetWorth")} showArrow={true}>
          <PanelContent>
            <StepPieChart
              width={241}
              height={241}
              rendererOverride="svg"
              data={filteredData}
              tooltipValueFormatter={tooltipValueFormatter}
              onChartClick={onPieChartClick}
              echartsOptions={{
                legend: {
                  show: false,
                },
                series: {
                  radius: "90%",
                },
              }}
            />
            <PieChartLegend onItemClick={(datum) => navigateToModule(datum)} pieSeries={filteredData} />
          </PanelContent>
        </Panel>
      </NetworthCollapse>
    </PortfolioCard>
  );
};

const NetworthCollapse = styled(PortfolioCollapse)`
  .ant-collapse-content {
    overflow: visible;
  }

  .ant-collapse-content > .ant-collapse-content-box {
    padding: 15px 0 !important;
  }

  @media (max-width: ${Breakpoints.M}px) {
    .ant-collapse-content > .ant-collapse-content-box {
      padding: 0 0 18px 0 !important;
    }

    .total-value-container {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      .net-worth {
        color: ${white};
        font-family: ${SPACE_MONO};
        font-size: 30px;
        font-weight: bold;
        line-height: 32px;
        margin-right: 10px;
      }
      .spinner-container {
        margin-left: 8px;
      }
    }
`;

const PanelContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 241px;
  padding: 0 20px;

  @media (max-width: ${Breakpoints.M}px) {
    flex-direction: column;
  }
`;

const eyeStyles = {
  fontSize: "1.2rem",
  color: transparentWhite,
};
